import { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export interface AgentLink {
  id: string;
  title: string;
  url: string;
  ai_instructions: string | null;
  created_at: string;
}

interface LinkEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveLink: (link: AgentLink) => void;
  initialLinkData?: AgentLink | null;
}

const LinkEditorModal: React.FC<LinkEditorModalProps> = ({
  isOpen,
  onClose,
  onSaveLink,
  initialLinkData
}) => {
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [aiInstructions, setAiInstructions] = useState("");
  const [errors, setErrors] = useState<{
    title?: string;
    url?: string;
  }>({});

  // Reset form and populate with initialLinkData when modal opens
  useEffect(() => {
    if (isOpen) {
      if (initialLinkData) {
        // Edit mode - populate form with existing data
        setTitle(initialLinkData.title || "");
        setUrl(initialLinkData.url || "");
        setAiInstructions(initialLinkData.ai_instructions || "");
      } else {
        // Create mode - reset form
        setTitle("");
        setUrl("");
        setAiInstructions("");
      }
      setErrors({});
    }
  }, [isOpen, initialLinkData]);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};
    
    if (!title.trim()) {
      newErrors.title = "Title is required";
    }
    
    if (!url.trim()) {
      newErrors.url = "URL is required";
    } else if (!isValidUrl(url)) {
      newErrors.url = "Please enter a valid URL";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const isValidUrl = (url: string): boolean => {
    try {
      // Add protocol if missing
      const urlToTest = url.match(/^https?:\/\//) ? url : `https://${url}`;
      new URL(urlToTest);
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }
    
    // Format the URL (add https:// if missing)
    let formattedUrl = url;
    if (!formattedUrl.match(/^https?:\/\//)) {
      formattedUrl = `https://${formattedUrl}`;
    }
    
    const linkData: AgentLink = {
      // If editing, keep the original ID and created_at, otherwise generate new ones
      id: initialLinkData?.id || uuidv4(),
      title: title.trim(),
      url: formattedUrl,
      ai_instructions: aiInstructions.trim() || null,
      created_at: initialLinkData?.created_at || new Date().toISOString()
    };
    
    onSaveLink(linkData);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {initialLinkData ? "Edit Link" : "Add New Link"}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="link-title" className="text-sm font-medium">
              Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="link-title"
              placeholder="Enter a title for this link"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={errors.title ? "border-red-500" : ""}
            />
            {errors.title && (
              <p className="text-xs text-red-500">{errors.title}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="link-url" className="text-sm font-medium">
              URL <span className="text-red-500">*</span>
            </Label>
            <Input
              id="link-url"
              placeholder="https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className={errors.url ? "border-red-500" : ""}
            />
            {errors.url && (
              <p className="text-xs text-red-500">{errors.url}</p>
            )}
            <p className="text-xs text-gray-500">
              The URL should include the protocol (http:// or https://)
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="link-ai-instructions" className="text-sm font-medium">
              AI Instructions
            </Label>
            <Textarea
              id="link-ai-instructions"
              placeholder="Instructions for how the AI should use this link (optional)"
              value={aiInstructions}
              onChange={(e) => setAiInstructions(e.target.value)}
              rows={3}
            />
            <p className="text-xs text-gray-500">
              Specific guidance for when and how the AI should use this resource
            </p>
          </div>
        </div>
        
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinkEditorModal; 